// src/components/annotation/Canvas.tsx

import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { drawAnnotations } from './utils/drawing'
import { Tag } from '../../types/models/tag'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import { MousePosition } from './hooks/useImageAnnotation'
import { isPointInOBB } from '../../utils/helpers'
import Spinner from 'react-bootstrap/Spinner'

export interface CanvasProps {
  canvasRef: RefObject<HTMLCanvasElement>
  imageRef: RefObject<HTMLImageElement>
  imageUrl: string | undefined
  handleMouseEvent: (
    e: React.MouseEvent<HTMLCanvasElement>,
    eventType: 'down' | 'move' | 'up' | 'click' | 'context'
  ) => void

  handleWheel: (e: React.WheelEvent<HTMLCanvasElement>) => void
  zoomLevel: number
  panOffset: MousePosition
  newBoundingBox: AnnotationDataBoundingBox | null
  selectedBBox: AnnotationDataBoundingBox | null
  onBoxSelect: (box: AnnotationDataBoundingBox | null) => void
  tags: { tags: Tag[] } | undefined
  useBBoxes: boolean
  showBBoxes: boolean
  mousePosition: MousePosition
  contrast: number
  // showHeatmap: boolean
  handleDoubleClick: (e: React.MouseEvent<HTMLCanvasElement>) => void
  showConfidence: boolean
  labelMode?: 'class' | 'class+confidence'
  canvasWidth: number //set to 0 to use image size
  canvasHeight: number
  bBoxIndex?: number
  filteredBoxes?: AnnotationDataBoundingBox[]
  hoveredBoxIndex?: number
  // OBB creation helpers
  creationAxisStart?: { x: number; y: number } | null
  creationPhase?: 'idle' | 'axisStart' | 'axisFixed'
  isEditable?: boolean
}

// (Unused) helper kept for reference; replaced by ResizeObserver responsive sizing
// const calculateCanvasDimensions = (imageWidth: number, imageHeight: number, maxDimension = 768) => {
//   const imageAspectRatio = imageWidth / imageHeight
//   let canvasWidth, canvasHeight
//   if (imageAspectRatio >= 1) {
//     canvasWidth = maxDimension
//     canvasHeight = Math.round(maxDimension / imageAspectRatio)
//   } else {
//     canvasHeight = maxDimension
//     canvasWidth = Math.round(maxDimension * imageAspectRatio)
//   }
//   return { canvasWidth, canvasHeight }
// }

export const Canvas: React.FC<CanvasProps> = ({
  canvasRef,
  imageRef,
  imageUrl,
  handleMouseEvent,
  handleWheel,
  zoomLevel,
  panOffset,
  newBoundingBox,
  selectedBBox,
  onBoxSelect,
  tags,
  useBBoxes,
  mousePosition,
  contrast,
  // showHeatmap,
  showBBoxes,
  handleDoubleClick,
  showConfidence,
  labelMode = 'class+confidence',
  canvasWidth,
  canvasHeight,
  bBoxIndex,
  filteredBoxes,
  hoveredBoxIndex,
  creationAxisStart,
  creationPhase,
  isEditable = true,
}) => {
  // Responsive canvas: occupy the largest square within the container (no hard-coded defaults)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: 0,
    height: 0,
  })
  const [isImageLoading, setIsImageLoading] = useState(true)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [allowBoundingBoxes, setAllowBoundingBoxes] = useState(false)
  const [isMouseOverCanvas, setIsMouseOverCanvas] = useState(false)
  const [canvasMousePosition, setCanvasMousePosition] = useState<MousePosition>({ x: 0, y: 0 })

  // Handle initial image loading
  useEffect(() => {
    if (imageUrl) {
      setIsImageLoading(true)
      setIsImageLoaded(false)
      setAllowBoundingBoxes(false)
    }
  }, [imageUrl])

  // Allow bounding boxes after a brief delay once image is loaded
  useEffect(() => {
    if (isImageLoaded) {
      const timer = setTimeout(() => {
        setAllowBoundingBoxes(true)
      }, 50) // Small delay to ensure image is rendered
      return () => clearTimeout(timer)
    }
  }, [isImageLoaded])

  // ResizeObserver to size canvas to the largest square within its container
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width)
        const h = Math.floor(entry.contentRect.height)
        // If height is 0/undefined (container not height-constrained), fall back to width
        const effectiveH = h > 0 ? h : w
        const size = Math.max(0, Math.min(w, effectiveH))
        setCanvasDimensions({ width: size, height: size })
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Memoize drawAnnotations call dependencies for performance
  const drawAnnotationsMemo = useCallback(() => {
    if (!isImageLoaded || !canvasRef.current || !imageRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    drawAnnotations(
      ctx,
      canvas,
      imageRef.current,
      zoomLevel,
      panOffset,
      filteredBoxes || [],
      showConfidence,
      tags?.tags || [],
      useBBoxes,
      showBBoxes && allowBoundingBoxes, // Only show bounding boxes if allowed
      newBoundingBox,
      selectedBBox,
      canvasMousePosition, // Use canvas coordinates for crosshairs
      contrast,
      // showHeatmap,
      bBoxIndex,
      hoveredBoxIndex,
      isMouseOverCanvas,
      creationAxisStart || null,
      creationPhase || 'idle',
      isEditable,
      labelMode
    )
  }, [
    isImageLoaded,
    allowBoundingBoxes,
    canvasRef,
    imageRef,
    zoomLevel,
    panOffset,
    filteredBoxes,
    tags,
    useBBoxes,
    showBBoxes,
    newBoundingBox,
    selectedBBox,
    canvasMousePosition,
    contrast,
    bBoxIndex,
    hoveredBoxIndex,
    isMouseOverCanvas,
    showConfidence,
    creationAxisStart,
    creationPhase,
    isEditable,
    labelMode,
  ])

  useEffect(() => {
    drawAnnotationsMemo()
  }, [drawAnnotationsMemo])

  const getCanvasMousePos = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): MousePosition => {
      if (!canvasRef.current) return { x: 0, y: 0 }

      const rect = canvasRef.current.getBoundingClientRect()
      const scaleX = canvasRef.current.width / rect.width
      const scaleY = canvasRef.current.height / rect.height

      const canvasX = (e.clientX - rect.left) * scaleX
      const canvasY = (e.clientY - rect.top) * scaleY

      return { x: canvasX, y: canvasY }
    },
    [canvasRef]
  )

  const getImageMousePos = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): MousePosition => {
      if (!canvasRef.current || !imageRef.current) return { x: 0, y: 0 }

      const rect = canvasRef.current.getBoundingClientRect()
      const scaleX = canvasRef.current.width / rect.width
      const scaleY = canvasRef.current.height / rect.height

      const canvasX = (e.clientX - rect.left) * scaleX
      const canvasY = (e.clientY - rect.top) * scaleY

      // Undo zoom/pan to get world coordinates in pre-transform canvas space
      const centerX = canvasRef.current.width / 2
      const centerY = canvasRef.current.height / 2
      const worldX = (canvasX - centerX) / zoomLevel + centerX - panOffset.x
      const worldY = (canvasY - centerY) / zoomLevel + centerY - panOffset.y

      // Compute contain-fit mapping for current canvas and image
      const imgW = imageRef.current.naturalWidth || 1
      const imgH = imageRef.current.naturalHeight || 1
      const scale = Math.min(canvasRef.current.width / imgW, canvasRef.current.height / imgH)
      const drawW = imgW * scale
      const drawH = imgH * scale
      const offsetX = (canvasRef.current.width - drawW) / 2
      const offsetY = (canvasRef.current.height - drawH) / 2

      // Map world canvas coords back to image coords via inverse of contain mapping
      const imgX = (worldX - offsetX) / scale
      const imgY = (worldY - offsetY) / scale

      return { x: imgX, y: imgY }
    },
    [canvasRef, imageRef, zoomLevel, panOffset.x, panOffset.y]
  )

  // Memoized event handlers
  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      console.log('naturalWidth', imageRef?.current?.naturalWidth)
      console.log('naturalHeight', imageRef?.current?.naturalHeight)
      console.log('canvasWidth', canvasRef?.current?.width)
      console.log('canvasHeight', canvasRef?.current?.height)

      handleMouseEvent(e, 'down')

      // Disable selecting existing boxes while creating a new one
      const isCreating = (creationPhase && creationPhase !== 'idle') || !!newBoundingBox
      // Also skip selection if Ctrl/Cmd held to force-create new boxes anywhere
      if (isCreating || e.ctrlKey || e.metaKey) return

      const { x, y } = getImageMousePos(e)
      const boxesToCheck = filteredBoxes ?? []
      const clickedBox = boxesToCheck.find((box) => {
        // Use points-based hit testing if available, otherwise fallback to min/max
        if (box.points && box.points.length === 8) {
          // Both testPoint and box.points are in pixel coordinates (UI coordinate system)
          // Don't pass image dimensions to avoid double conversion
          return isPointInOBB({ x, y }, box.points)
        } else {
          // Legacy hit testing for boxes without points
          return (
            x >= (box.xmin ?? 0) &&
            x <= (box.xmax ?? 0) &&
            y >= (box.ymin ?? 0) &&
            y <= (box.ymax ?? 0)
          )
        }
      })
      onBoxSelect(clickedBox || null)
    },
    [
      canvasRef,
      imageRef,
      handleMouseEvent,
      getImageMousePos,
      filteredBoxes,
      onBoxSelect,
      creationPhase,
      newBoundingBox,
    ]
  )

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault()
      // Update canvas mouse position for crosshairs
      const canvasPos = getCanvasMousePos(e)
      setCanvasMousePosition(canvasPos)

      handleMouseEvent(e, 'move')
    },
    [handleMouseEvent, getCanvasMousePos]
  )

  const handleCanvasMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      handleMouseEvent(e, 'up')
    },
    [handleMouseEvent]
  )

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      handleMouseEvent(e, 'click')
    },
    [handleMouseEvent]
  )

  const handleCanvasContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      handleMouseEvent(e, 'context')
    },
    [handleMouseEvent]
  )

  const handleImageLoad = useCallback(() => {
    if (!canvasRef.current || !imageRef.current) return

    setIsImageLoaded(true)
    setIsImageLoading(false)
  }, [canvasRef, imageRef])

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        width={Math.round(canvasDimensions.width * (window.devicePixelRatio || 1))}
        height={Math.round(canvasDimensions.height * (window.devicePixelRatio || 1))}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onClick={handleCanvasClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleCanvasContextMenu}
        onWheel={handleWheel}
        style={{
          userSelect: 'none',
          width: `${canvasDimensions.width}px`,
          height: `${canvasDimensions.height}px`,
          display: 'block',
          visibility: isImageLoading ? 'hidden' : 'visible',
        }}
        onMouseEnter={() => setIsMouseOverCanvas(true)}
        onMouseLeave={() => setIsMouseOverCanvas(false)}
        role="img"
        aria-label="Annotation Canvas"
      />
      <img
        ref={imageRef}
        src={imageUrl}
        alt="Annotate"
        style={{ display: 'none' }}
        onLoad={handleImageLoad}
        crossOrigin="anonymous"
      />
      {isImageLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10,
          }}
          aria-live="polite"
        >
          <Spinner animation="border" variant="light" role="status" aria-label="Loading image">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}
    </div>
  )
}

export default Canvas
